





































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  toRefs,
  useSlots,
  watch
} from '~/utils/nuxt3-migration'
import ScrollService from '~/services/scroll/ScrollService'
import { useDep } from '~/compositions/dependency-container'
import { useSwipe } from '~/compositions/swipe'
import { ciAngleLeft } from '~/icons/source/light/angle-left'
import { ciAngleRight } from '~/icons/source/light/angle-right'

export default defineComponent({
  props: {
    loop: {
      type: Boolean,
      default: false
    },
    centered: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const { loop } = toRefs(props)
    const focusedIndex = ref<number>(0)
    const slotsLength = ref<number | null>(null)

    const slots = useSlots()
    const containerRef = ref(null)
    const scrollService = useDep(ScrollService)

    onMounted(() => {
      initializeSlots()
    })

    const initializeSlots = () => {
      const theSlots = slots.default()
      slotsLength.value = theSlots.length

      // add click event
      for (let i = 0; i < theSlots.length; i++) {
        const slot = theSlots[i].elm as HTMLElement

        if (slot) {
          if (i === 0) {
            // add margin left equal to its width
            slot.style.cssText = `margin-left: ${getMarginValueForEdgeSlot(
              slot
            )}px`
          } else if (i === theSlots.length - 1) {
            // add margin right equal to its width
            slot.style.cssText = `margin-right: ${getMarginValueForEdgeSlot(
              slot
            )}px`
          }
          slot.addEventListener(
            'click',
            e => {
              slotOnClick(e, i)
            },
            true // true means this will always run first
          )
        }
      }

      // update
      updateSlotClasses()

      setFocusedIndex(0) // in order to move first one to correct position
    }

    const getMarginValueForEdgeSlot = (slot: HTMLElement) => {
      // we want half of the container minus half of the element (?)
      if (props.centered) {
        if (containerRef.value && props.centered) {
          return containerRef.value.offsetWidth / 2 - slot.offsetWidth / 2
        }
        return slot.offsetWidth
      }
      return 0
    }

    const onSwipeLeft = () => {
      focusNext()
    }
    const onSwipeRight = () => {
      focusPrevious()
    }

    // add swipe functionality
    const { getDragging } = useSwipe(
      containerRef,
      onSwipeLeft,
      onSwipeRight,
      true
    )

    const slotOnClick = (e: Event, i: number) => {
      if (i !== focusedIndex.value || getDragging()) {
        e.preventDefault()
        e.stopPropagation()

        if (i !== focusedIndex.value && !getDragging()) {
          setFocusedIndex(i)
        }
      }
    }

    const setFocusedIndex = (newIndex: number) => {
      focusedIndex.value = newIndex
      emit('focused-index', newIndex)

      const theSlots = slots.default()
      const element = theSlots[newIndex].elm
      const offset = getMarginValueForEdgeSlot(element)
      scrollService.scrollIfOverflown(element, containerRef.value, offset)
    }

    const updateSlotClasses = () => {
      // WARNING: in order for this to work there needs to be no dynamic class in slot components
      // if you want to also have dynamic classes to your slot components then you can
      // use the emited focusedIndex and feed it to the component for styling

      const theSlots = slots.default()
      for (let i = 0; i < theSlots.length; i++) {
        const slot = theSlots[i].elm
        if (i === focusedIndex.value) {
          slot.classList.remove('swipe-focused')
          slot.classList.add('swipe-focused')
        } else {
          slot.classList.remove('swipe-focused')
        }
      }
    }

    watch(focusedIndex, () => {
      updateSlotClasses()
    })

    const focusNext = () => {
      if (focusedIndex.value + 1 <= slotsLength.value - 1) {
        setFocusedIndex(focusedIndex.value + 1)
      } else if (loop.value) {
        setFocusedIndex(0)
      }
    }
    const focusPrevious = () => {
      if (focusedIndex.value - 1 >= 0) {
        setFocusedIndex(focusedIndex.value - 1)
      } else if (loop.value) {
        setFocusedIndex(slotsLength.value - 1)
      }
    }

    const prevButtonDisabled = computed(() => {
      if (!loop.value) {
        return focusedIndex.value === 0
      }
      return false
    })
    const nextButtonDisabled = computed(() => {
      if (!loop.value) {
        return focusedIndex.value === slotsLength.value - 1
      }
      return false
    })

    return {
      updateSlotClasses,
      focusedIndex,
      ciAngleLeft,
      ciAngleRight,
      focusNext,
      focusPrevious,
      slotsLength,
      initializeSlots,
      slotOnClick,
      containerRef,
      getDragging,
      prevButtonDisabled,
      nextButtonDisabled
    }
  }
})
